/**
 * Wishlist Modal functionality for Collection Tabs
 * Handles modal open/close, swiper initialization, and variant selection
 */

class WishlistModal {
  constructor() {
    this.activeModal = null;
    this.swiperInstance = null;
    // Card showing the loading spinner while its product fetch is in flight
    this.loadingHandle = null;
    this.loadingCard = null;
    // Campaign badge on the lightbox price. Set per open from the tapped card's
    // mount (applyCampaignConfig) — blank text hides the badge.
    this.promoBadgeText = '';
    this.promoBadgeColor = '#C9FE34';
    this.promoBadgeTextColor = '#000000';
    this.init();
  }

  // Section/block settings for the wishlist grid the tapped card sits in, written
  // by snippets/wishlist-lightbox-config.liquid as data attributes on the tab or
  // mount. Read per open so two tabs on one page keep their own badge and colours.
  // A ?wishlist=true page has no config element and falls back to the defaults.
  applyCampaignConfig(modal, trigger) {
    // A Complete the Look card inside the lightbox has no config of its own:
    // the product it opens keeps the tab's badge and colours.
    if (trigger && modal.contains(trigger)) return;

    const config = (trigger && trigger.closest('[data-wishlist-config]')) || null;
    const data = config ? config.dataset : {};

    this.promoBadgeText = data.wishlistBadgeText || '';
    this.promoBadgeColor = data.wishlistBadgeColor || '#C9FE34';
    this.promoBadgeTextColor = data.wishlistBadgeTextColor || '#000000';

    const setVar = (name, value) => {
      if (value) modal.style.setProperty(name, value);
      else modal.style.removeProperty(name);
    };
    setVar('--wishlist-size-banner-bg', data.wishlistPrimaryColor);
    setVar('--wishlist-size-banner-text', data.wishlistSecondaryColor);
    setVar('--wishlist-button-bg', data.wishlistButtonColor);
    setVar('--wishlist-button-text', data.wishlistButtonTextColor);
  }

  // On a phone, the card heart in wishlist mode opens this lightbox like ADD TO
  // WISHLIST does. Left alone, component-list-card-product.js would open the
  // card's own size list. Desktop keeps the hover list. A one-variant product
  // (a supplement, a one-size accessory) has no size to pick, so its heart is
  // left to component-list-card-product.js, which saves it as on desktop.
  phoneWishlistHeart(target) {
    const heart = target.closest('button.wishlistButton');
    if (!heart || !heart.closest('.wishlist-lightbox')) return null;
    if (heart.classList.contains('backup-addToWishlist')) return null;
    return window.matchMedia('(max-width: 767px)').matches ? heart : null;
  }

  // The card's ADD TO WISHLIST bar for a one-variant product
  // (card-product-media.liquid gives it backup-addToWishlist and the variant id).
  isOneVariantBar(element) {
    return element.matches('.backupWishlistButton.backup-addToWishlist[data-variant-id]');
  }

  init() {
    // Listen for clicks on backupWishlistButton - always open new wishlist modal
    // Use capture phase to intercept event before other listeners
    document.addEventListener('click', (e) => {
      const backupWishlistButton = e.target.closest('.backupWishlistButton') || this.phoneWishlistHeart(e.target);

      if (backupWishlistButton) {
        // Only take over the click for Liquid cards. React-rendered cards
        // (src/collection, mounted into [data-ss-root] or straight into
        // [data-component="SSCollection"] by collection-tabs-athos.liquid) reuse
        // this class for styling only and handle the click themselves
        // (AddToCartButton.tsx's WishlistVariantBanner/ComingSoonWishlistButton).
        // They have no Liquid card wrapper, so bail out before swallowing the event.
        const listCardProduct = backupWishlistButton.closest('list-card-product, .card-product');
        if (!listCardProduct) return;

        // Always prevent default behavior and open the new modal
        e.preventDefault();
        e.stopPropagation();
        e.stopImmediatePropagation(); // Prevent other listeners from firing

        // Nothing to pick on a one-variant product, so its bar saves from the card
        if (this.isOneVariantBar(backupWishlistButton)) {
          this.saveFromCard(backupWishlistButton, listCardProduct);
          return;
        }

        // Prefer the data-product-handle/handle attributes Liquid already renders
        // on the card (snippets/card-product.liquid) — the .card-info-link scrape
        // below is a fallback for markup without them (e.g. bare React cards).
        // This button (backup-hasVariants, mobile) has no hover state to fall
        // back on, so a missed handle here must not silently swallow the tap.
        const cardWithHandle = backupWishlistButton.closest('[data-product-handle], list-card-product[handle]');
        let productHandle = cardWithHandle
          ? (cardWithHandle.dataset.productHandle || cardWithHandle.getAttribute('handle'))
          : null;

        if (!productHandle) {
          const productLink = listCardProduct.querySelector('.card-info-link, a[href*="/products/"]');
          const href = productLink && productLink.getAttribute('href');
          const handleMatch = href && href.match(/\/products\/([^/?]+)/);
          if (handleMatch) productHandle = handleMatch[1];
        }

        if (!productHandle) return;

        // Open the modal
        this.openModal(productHandle, backupWishlistButton);
        return;
      }
    }, true); // Use capture phase to catch event first
    
    // Listen for clicks on product cards in wishlist lightbox mode
    document.addEventListener('click', (e) => {
      // Don't trigger modal if clicking on buttons or other interactive elements (check first)
      if (e.target.closest('button, input, select, textarea, .backupWishlistButton, .cartButton')) {
        return;
      }
      
      // Check if we're inside a wishlist-lightbox scope. On a tabbed page
      // (sections/collection-tabs-athos.liquid, collection-tabs.liquid) that
      // scope is a .tab-content, which must also be the currently-visible tab —
      // on a non-tabbed page (sections/ss-collection.liquid) there is no
      // tab-switching concept to gate on, so any .wishlist-lightbox wrapper qualifies.
      // The scope can be the React grid wrapper inside a tab, so gate on the
      // enclosing .tab-content rather than on the scope element itself.
      const scope = e.target.closest('.wishlist-lightbox');
      if (!scope) return;
      const tab = e.target.closest('.tab-content');
      if (tab && !tab.classList.contains('visible')) return;
      
      // Check if click is on a list-card-product element (Liquid card-product markup)
      // or a .productCard (React ProductCard.tsx — used by collection-tabs-athos.liquid)
      const listCardProduct = e.target.closest('list-card-product, .productCard');
      if (!listCardProduct) return;

      // Linked-PDP cards (`pdp-link:<handle>` tag, e.g. the Hydration flavours)
      // stay out of the wishlist: their link points at the container product, so
      // the lightbox would save the container instead of the flavour. Let the tap
      // navigate to the container PDP with ?flavour= preselected. Both the React
      // card and the Liquid card (card-product-media.liquid) mark them with
      // [data-card-actions][data-linked-pdp]. The React card also marks its root
      // (ProductCard.tsx), since its card actions render only after the variant
      // fetch and a quick tap would otherwise open the lightbox.
      if (
        listCardProduct.matches('[data-linked-pdp]') ||
        listCardProduct.querySelector('[data-card-actions][data-linked-pdp]')
      ) return;

      // shop-now cards (Hydration Sticks container, Greens Gummies) save from
      // their PDP too: card-product-media.liquid marks them data-wishlist-pdp
      // and their bar links there, so the photo goes there as well.
      if (listCardProduct.querySelector('[data-card-actions][data-wishlist-pdp]')) return;

      // no-wishlist products have no heart and no ADD TO WISHLIST on the card,
      // so the card tap mustn't save them through the lightbox either. Both
      // cards mark their root (ProductCard.tsx, card-product.liquid).
      if (
        listCardProduct.matches('[data-no-wishlist]') ||
        listCardProduct.querySelector('.productCardRoot[data-no-wishlist]')
      ) return;

      // Get the card-product inside, or the element itself if it already is one
      const productCard = listCardProduct.matches('.card-product, .productCard')
        ? listCardProduct
        : listCardProduct.querySelector('.card-product, .productCard');
      if (!productCard) return;

      // Prevent default link behavior and open modal
      e.preventDefault();
      e.stopPropagation();

      // The card's own handle first: a one-variant pdp-link card (a Hydration
      // flavour, card-product-media.liquid's wishlist_on_card) links to the
      // container's PDP, and the lightbox must open the flavour.
      const productHandle = this.getCardProductHandle(listCardProduct);
      if (!productHandle) return;

      // Find and open the modal
      this.openModal(productHandle, listCardProduct);
    });
    
    // Complete the Look cards in the extra content link their photo and title
    // to the PDP. Inside the lightbox a tap opens that product here instead,
    // like a card on the grid, rather than leaving the page. The card's colour
    // swatches stay with assets/list-card-product-swatches.js, which swaps the
    // card in place. A product saved on its PDP (data-wishlist-pdp, set by
    // list-card-product.liquid for pdp-link and shop-now with options) keeps
    // its links to that PDP. The card's own handle (data-product-handle) wins
    // over the link's: a one-variant pdp-link card (a Hydration flavour) links
    // to the container, and the lightbox must open the flavour.
    document.addEventListener('click', (e) => {
      const link = e.target.closest('[data-wishlist-slot] list-card-product a[href*="/products/"]');
      if (!link || link.matches('[data-list-card-swatch]')) return;
      if (link.closest('[data-list-card-root][data-wishlist-pdp]')) return;
      if (e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
      const root = link.closest('[data-list-card-root][data-product-handle]');
      const handleMatch = link.getAttribute('href').match(/\/products\/([^/?#]+)/);
      const productHandle = (root && root.dataset.productHandle) || (handleMatch && handleMatch[1]);
      if (!productHandle) return;
      e.preventDefault();
      e.stopPropagation();
      this.openModal(productHandle, link);
    }, true);

    // Listen for close button clicks
    document.addEventListener('click', (e) => {
      const closeButton = e.target.closest('.wishlist-modal__close');
      if (closeButton) {
        e.preventDefault();
        e.stopPropagation();
        this.closeModal();
      }
    });
    
    // Listen for overlay clicks
    document.addEventListener('click', (e) => {
      if (e.target.classList.contains('wishlist-modal__overlay')) {
        e.preventDefault();
        e.stopPropagation();
        this.closeModal();
      }
    });
    
    // Listen for ESC key
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this.activeModal) {
        this.closeModal();
      }
    });

    // Start the product fetch when a tap ends rather than at the click that
    // follows it, so openModal's fetch is already in flight. Only a tap counts:
    // a finger that lands on a card to scroll the grid moves before it lifts,
    // and prefetching on touchstart sent a product and a lightbox render for
    // every card a scroll touched. Passive and best-effort: a miss here just
    // means openModal fetches fresh.
    let tapStart = null;
    document.addEventListener('touchstart', (e) => {
      const touch = e.touches.length === 1 ? e.touches[0] : null;
      tapStart = touch ? { x: touch.clientX, y: touch.clientY, target: e.target } : null;
    }, { passive: true });
    document.addEventListener('touchmove', (e) => {
      if (!tapStart) return;
      const touch = e.touches[0];
      if (Math.abs(touch.clientX - tapStart.x) > 10 || Math.abs(touch.clientY - tapStart.y) > 10) tapStart = null;
    }, { passive: true });
    document.addEventListener('touchcancel', () => { tapStart = null; }, { passive: true });
    document.addEventListener('touchend', () => {
      const start = tapStart;
      tapStart = null;
      if (start) this.prefetchProduct(this.tapProductHandle(start.target));
    }, { passive: true });
    
    // Listen for show more button clicks
    document.addEventListener('click', (e) => {
      if (e.target.classList.contains('wishlist-modal__show-more')) {
        this.toggleDescription(e.target);
      }
    });
    
    // Listen for variant selection changes
    document.addEventListener('change', (e) => {
      if (e.target.closest('.wishlist-modal__variants')) {
        // Another length of a single-length product is another product (see
        // addSiblingValues): swap the lightbox to it.
        if (e.target.dataset.siblingHandle) {
          this.switchToSibling(e.target);
          return;
        }
        this.handleVariantChange(e.target);
      }
    });
    
    // Listen for wishlist button clicks
    document.addEventListener('click', (e) => {
      if (e.target.closest('.wishlist-modal__button.selectForWishlist')) {
        this.handleAddToWishlist(e.target.closest('.wishlist-modal__button'));
      }
    });
  }
  
  areRequiredVariantOptionsSelected(modal) {
    const variantsContainer = modal.querySelector('.wishlist-modal__variants');
    
    // If no variants container or it's hidden, no selection is required
    if (!variantsContainer || variantsContainer.style.display === 'none') {
      return true;
    }
    
    // Check all fieldsets for checked radios
    const fieldsets = variantsContainer.querySelectorAll('.wishlist-modal__variant-fieldset');
    
    for (let fieldset of fieldsets) {
      const hasCheckedRadio = fieldset.querySelector('input:checked');
      if (!hasCheckedRadio) {
        return false;
      }
    }
    
    return true;
  }
  
  getVariantSelectionMessage(modal) {
    const variantsContainer = modal.querySelector('.wishlist-modal__variants');
    
    if (!variantsContainer) {
      return 'Please select a product option.';
    }
    
    let unselectedOptions = [];
    const fieldsets = variantsContainer.querySelectorAll('.wishlist-modal__variant-fieldset');
    
    fieldsets.forEach(fieldset => {
      const legend = fieldset.querySelector('.wishlist-modal__variant-label');
      const hasCheckedRadio = fieldset.querySelector('input:checked');
      
      if (!hasCheckedRadio && legend) {
        unselectedOptions.push(legend.textContent.trim());
      }
    });
    
    if (unselectedOptions.length > 0) {
      // Create specific message based on unselected options
      if (unselectedOptions.length === 1) {
        return `Pick a ${unselectedOptions[0]}!`;
      } else {
        const lastOption = unselectedOptions.pop();
        return `Pick ${unselectedOptions.join(', ')} and ${lastOption}!`;
      }
    }
    
    // Fallback message
    return 'Please select a product option.';
  }
  
  handleErrorMessage(modal, errorMessage = false) {
    let errorWrapper = modal.querySelector('.wishlist-modal__error-message');
    
    if (!errorWrapper) {
      // Create error message element if it doesn't exist
      errorWrapper = document.createElement('div');
      errorWrapper.className = 'wishlist-modal__error-message';
      errorWrapper.style.cssText = `
        color: #dc3545;
        background: #f8d7da;
        border: 1px solid #f5c6cb;
        padding: 12px 16px;
        margin-bottom: 16px;
        border-radius: 4px;
        font-size: 14px;
        display: none;
      `;
      
      // Insert before the wishlist button
      const button = modal.querySelector('.selectForWishlist');
      if (button && button.parentNode) {
        button.parentNode.insertBefore(errorWrapper, button);
      }
    }
    
    if (errorMessage) {
      errorWrapper.textContent = errorMessage;
      errorWrapper.style.display = 'block';
    } else {
      errorWrapper.style.display = 'none';
    }
  }

  // ADD TO WISHLIST on a one-variant card saves that variant without the
  // lightbox. The store shows the save straight away, and the card turns the
  // bar into ADDED TO WISHLIST and fills the heart (markButtonAsSaved in
  // component-list-card-product.js), or turns them back if the save fails.
  // Like the lightbox, the bar only ever adds; the heart is what unsaves.
  async saveFromCard(bar, listCardProduct) {
    const store = window.MNWishlistStore;
    const variantId = bar.dataset.variantId;
    const heart = listCardProduct.querySelector('button.wishlistButton[data-product-id]');
    const productId = bar.dataset.productId || (heart && heart.dataset.productId);
    const productHandle = bar.dataset.handle || (heart && heart.dataset.handle) || this.getCardProductHandle(listCardProduct);

    if (!store || !productId) {
      if (productHandle) this.openModal(productHandle, bar);
      return;
    }
    if (store.has(null, variantId)) return;

    try {
      await store.add({ productHandle, productId, variantId });
    } catch (error) {
      console.error('Error adding to wishlist:', error);
      this.showNotification('Failed to add item to wishlist. Please try again.', 'error');
      return;
    }

    // A lightbox opened since keeps the guest login prompt until it closes
    if (this.activeModal) this.promptOnClose = true;
    else window.MN?.maybeShowWishlistLoginPrompt?.();
  }

  // The product a tap on this element would open in the lightbox, for the tap
  // prefetch: an ADD TO WISHLIST bar or phone heart, or a card in a visible
  // wishlist tab. A one-variant bar saves without opening, so it has none.
  tapProductHandle(target) {
    const backupWishlistButton = target.closest('.backupWishlistButton') || this.phoneWishlistHeart(target);
    if (backupWishlistButton && this.isOneVariantBar(backupWishlistButton)) return null;
    let listCardProduct = null;
    if (backupWishlistButton) {
      listCardProduct = backupWishlistButton.closest('list-card-product, .card-product');
    } else if (target.closest('.wishlist-lightbox')) {
      const tab = target.closest('.tab-content');
      if (!tab || tab.classList.contains('visible')) {
        listCardProduct = target.closest('list-card-product, .productCard');
      }
    }
    return listCardProduct ? this.getCardProductHandle(listCardProduct) : null;
  }

  // Best-effort handle read for the tap prefetch — not as strict as the
  // click handlers' own precedence (data-linked-pdp/data-no-wishlist aren't
  // checked here), since a wrong prefetch only wastes a request; openModal's
  // click handlers still decide what actually opens.
  getCardProductHandle(listCardProduct) {
    const cardWithHandle = listCardProduct.closest('[data-product-handle], list-card-product[handle]');
    const attrHandle = cardWithHandle && (cardWithHandle.dataset.productHandle || cardWithHandle.getAttribute('handle'));
    if (attrHandle) return attrHandle;

    const productLink = listCardProduct.querySelector('.card-info-link, a[href*="/products/"]');
    const href = productLink && productLink.getAttribute('href');
    const handleMatch = href && href.match(/\/products\/([^/?]+)/);
    return handleMatch ? handleMatch[1] : null;
  }

  // /products/<handle>.js, cached per handle like fetchExtraContent. Called
  // as a tap ends, ahead of the click, and from openModal itself as a
  // fallback for non-touch input (mouse click, keyboard).
  prefetchProduct(productHandle) {
    if (!productHandle) return;
    this.productCache = this.productCache || new Map();
    if (this.productCache.has(productHandle)) return;
    const request = fetch(`/products/${productHandle}.js`)
      .then((response) => {
        if (!response.ok) throw new Error(`Product not found: ${productHandle}`);
        return response.json();
      })
      .catch((error) => {
        this.productCache.delete(productHandle);
        throw error;
      });
    this.productCache.set(productHandle, request);
    // Warm the extra-content section render too. A tap that opens nothing
    // leaves both unawaited: openModal reports their errors when it awaits them.
    request.catch(() => {});
    this.fetchExtraContent(productHandle).catch(() => {});
  }

  // swap: replace the product in the open lightbox (a length switch) instead
  // of closing it and opening a new one. Keeps its colours and scroll position.
  async openModal(productHandle, trigger, { swap = false } = {}) {
    const modal = document.getElementById('CollectionTabsWishlistModal');
    
    if (!modal) {
      console.error('Wishlist modal not found');
      return;
    }
    
    // A repeat tap on the card that is already loading does nothing.
    if (this.loadingHandle === productHandle) return;

    // Close any existing modal first
    if (this.activeModal && !swap) {
      this.closeModal();
    }

    if (!swap) this.applyCampaignConfig(modal, trigger);

    // The lightbox only opens once /products/<handle>.js returns, so show a
    // spinner on the tapped card meanwhile — without it the tap looks ignored.
    const card = this.setCardLoading(trigger, productHandle);

    // Started alongside the product fetch rather than after it, so the extra
    // content (reviews, model details, fabric, Complete the Look...) doesn't
    // add its own round trip on top of the product fetch the sheet already
    // waits on. /products/<handle>.js carries no metafields/reviews, hence
    // the separate Section Rendering API request.
    const extraContentPromise = this.fetchExtraContent(productHandle);

    // Fetch product data using handle — prefetchProduct is a no-op if
    // touchstart already started (or finished) this same request.
    this.prefetchProduct(productHandle);

    try {
      const product = await this.productCache.get(productHandle);

      // Another card was tapped while this one loaded: that one wins.
      if (this.loadingHandle !== productHandle) return;

      // A swap rebuilds the slides, so the old Swiper goes first.
      if (swap && this.swiperInstance) {
        this.swiperInstance.destroy(true, true);
        this.swiperInstance = null;
      }

      this.populateModal(modal, product, this.cardSubtitle(trigger, productHandle));
      if (swap) this.applyCarriedPicks(modal);

      // Counts opens, so a save that answers late can tell whether the
      // lightbox still shows what it saved (handleAddToWishlist).
      this.openId = (this.openId || 0) + 1;
      this.activeModal = modal;
      this.activeProductHandle = productHandle;
      this.applyExtraContent(modal, extraContentPromise, productHandle, this.openId);

      modal.style.display = 'flex';
      modal.classList.add('active');
      document.body.classList.add('wishlist-modal-open');
      this.trackProductView(product);
      
      // Reset scroll position to top, except on a length swap: the shopper is
      // at the size buttons and stays there.
      const modalContainer = modal.querySelector('.wishlist-modal__container');
      const modalInfoScroll = modal.querySelector('.wishlist-modal__info-scroll');
      if (modalContainer && !swap) {
        modalContainer.scrollTop = 0;
      }
      if (modalInfoScroll && !swap) {
        modalInfoScroll.scrollTop = 0;
      }
      
      // Initialize Swiper after modal is visible and DOM is updated.
      // initSwiper loads the Swiper library itself when it isn't on the page yet.
      requestAnimationFrame(() => {
        setTimeout(() => {
          this.initSwiper(modal);
        }, 150);
      });
      
    } catch (error) {
      console.error('Error loading product:', error);
    } finally {
      this.clearCardLoading(card, productHandle);
    }
  }

  // A product shown here counts as a product view, like a PDP load. On a PDP
  // the Stape app pushes view_item_stape, and GTM fires Meta and TikTok
  // ViewContent, Snap VIEW_CONTENT, GA4 view_item and the Stape server tag on
  // it. The lightbox never loads a PDP, so it pushes the same event with the
  // ecommerce fields those tags read. user_data and cart_state stay as the
  // page's page_view_stape set them. Fires again for a length swap or a
  // Complete the Look tap: each shows another product.
  trackProductView(product) {
    const variants = product.variants || [];
    const variant = variants.find((v) => v.available) || variants[0];
    if (!variant) return;
    const price = (variant.price / 100).toFixed(2);
    window.dataLayer = window.dataLayer || [];
    // GTM merges each push into what it already holds, item by item, so the
    // previous product's fields would leak into this one without the reset.
    window.dataLayer.push({ ecommerce: null });
    window.dataLayer.push({
      event: 'view_item_stape',
      ecommerce: {
        value: price,
        currency: window.Shopify && window.Shopify.currency ? window.Shopify.currency.active : undefined,
        items: [{
          item_id: String(product.id),
          item_sku: variant.sku || '',
          item_variant: String(variant.id),
          item_name: product.title,
          variant_name: variant.title,
          item_category: product.type,
          price: Number(price),
          item_brand: product.vendor,
          imageURL: product.featured_image,
          item_url: `/products/${product.handle}`,
          quantity: '1',
        }],
      },
    });
  }

  setCardLoading(trigger, productHandle) {
    if (this.loadingCard) this.loadingCard.classList.remove('wishlist-card--loading');
    this.loadingHandle = productHandle;

    const card = trigger && (trigger.closest('.productCardRoot') || trigger.closest('list-card-product'));
    this.loadingCard = card || null;
    if (card) {
      card.classList.add('wishlist-card--loading');
      card.setAttribute('aria-busy', 'true');
    }
    return card;
  }

  clearCardLoading(card, productHandle) {
    if (card) {
      card.classList.remove('wishlist-card--loading');
      card.removeAttribute('aria-busy');
    }
    if (this.loadingHandle === productHandle) {
      this.loadingHandle = null;
      this.loadingCard = null;
    }
  }

  // sections/wishlist-lightbox-content.liquid rendered through the Section
  // Rendering API — reviews, model details, fabric, Complete the Look, etc.
  // Cached per handle: two cards for the same product (e.g. a colour switch)
  // reuse the same request instead of re-fetching.
  fetchExtraContent(productHandle) {
    this.extraContentCache = this.extraContentCache || new Map();
    if (!this.extraContentCache.has(productHandle)) {
      const request = fetch(`/products/${productHandle}?section_id=wishlist-lightbox-content`)
        .then((response) => {
          if (!response.ok) throw new Error(`Section request failed: ${response.status}`);
          return response.text();
        })
        .catch((error) => {
          this.extraContentCache.delete(productHandle);
          throw error;
        });
      this.extraContentCache.set(productHandle, request);
    }
    return this.extraContentCache.get(productHandle);
  }

  // Each [data-wishlist-part] of the section goes into the [data-wishlist-slot]
  // of the same name (snippets/collection-tabs-wishlist-modal.liquid), so
  // reviews land above the title and the rest around the size buttons and
  // description. A part the product doesn't have leaves its slot empty.
  async applyExtraContent(modal, extraContentPromise, productHandle, openId) {
    const slots = modal.querySelectorAll('[data-wishlist-slot]');
    if (!slots.length) return;
    slots.forEach((slot) => { slot.innerHTML = ''; });
    try {
      const html = await extraContentPromise;

      // The modal is reused for every product. If a slow request for this
      // product resolves after a faster tap on another card already opened
      // (or closed) it, this content must not land in that other sheet. The
      // same product closed and opened again shares the cached request, so
      // only the latest open fills the slots; both filling them showed every
      // part twice.
      if (this.openId !== openId || this.activeProductHandle !== productHandle) return;

      const parsed = new DOMParser().parseFromString(html, 'text/html');
      parsed.querySelectorAll('[data-wishlist-part]').forEach((part) => {
        const slot = modal.querySelector(`[data-wishlist-slot="${part.dataset.wishlistPart}"]`);
        if (slot) slot.appendChild(part);
      });

      slots.forEach((slot) => this.runInertScripts(slot));

      const siblings = parsed.querySelector('[data-wishlist-siblings]');
      if (siblings) {
        try {
          this.addSiblingValues(modal, JSON.parse(siblings.textContent), productHandle);
        } catch (error) {
          console.error('Error reading the other lengths:', error);
        }
      }

      const toggle = modal.querySelector('[data-wishlist-slot] [data-wishlist-size-guide-toggle]');
      const panel = modal.querySelector('[data-wishlist-slot] [data-wishlist-size-guide]');
      if (toggle && panel) {
        toggle.addEventListener('click', () => this.toggleSizeGuide(toggle, panel));
      }
    } catch (error) {
      // Best-effort: the sheet still works for saving a size without this content.
      console.error('Error loading wishlist lightbox content:', error);
    }
  }

  // A single-length product (a combined-listing child such as Aura Straight Leg
  // Leggings Tall) only owns its own length, so its Length row shows one value.
  // sections/wishlist-lightbox-content.liquid lists the other lengths' products;
  // they go in the row in the store's order and swap the lightbox when picked.
  addSiblingValues(modal, data, productHandle) {
    if (this.activeProductHandle !== productHandle || !data || !Array.isArray(data.values)) return;
    const name = String(data.option || '').trim().toLowerCase();
    const fieldset = [...modal.querySelectorAll('.wishlist-modal__variant-fieldset')].find(
      (f) => (f.querySelector('.wishlist-modal__variant-label')?.textContent || '').trim().toLowerCase() === name
    );
    const optionsDiv = fieldset && fieldset.querySelector('.wishlist-modal__variant-options');
    const ownLabel = optionsDiv && optionsDiv.querySelector('.wishlist-modal__variant-button');
    const ownInput = ownLabel && ownLabel.querySelector('input');
    if (!ownInput || optionsDiv.querySelector('[data-sibling-handle]')) return;

    const labels = data.values.map((value) => {
      if (value.handle === productHandle || value.name === ownInput.value) return ownLabel;
      if (!value.handle) return null;
      const label = document.createElement('label');
      label.className = 'wishlist-modal__variant-button';
      const input = document.createElement('input');
      input.type = 'radio';
      input.name = ownInput.name;
      input.value = value.name;
      input.dataset.siblingHandle = value.handle;
      const span = document.createElement('span');
      span.textContent = value.name;
      label.appendChild(input);
      label.appendChild(span);
      // Warm it, so the swap is instant
      this.prefetchProduct(value.handle);
      return label;
    }).filter(Boolean);
    if (labels.length < 2) return;
    labels.forEach((label) => optionsDiv.appendChild(label));
  }

  // Swap to another length's product, keeping the shopper's other picks (size).
  switchToSibling(input) {
    const modal = input.closest('.wishlist-modal');
    const fieldset = input.closest('.wishlist-modal__variant-fieldset');
    if (!modal || !fieldset) return;
    this.carriedPicks = [...modal.querySelectorAll('.wishlist-modal__variant-fieldset')]
      .filter((f) => f !== fieldset)
      .map((f) => ({
        option: (f.querySelector('.wishlist-modal__variant-label')?.textContent || '').trim(),
        value: f.querySelector('input:checked')?.value,
      }))
      .filter((pick) => pick.value);
    // Keep this product's own length picked until the other one is on screen,
    // so the lightbox still saves something real if the swap fails.
    const own = fieldset.querySelector('input:not([data-sibling-handle])');
    if (own) own.checked = true;
    this.openModal(input.dataset.siblingHandle, input, { swap: true });
  }

  applyCarriedPicks(modal) {
    const picks = this.carriedPicks || [];
    this.carriedPicks = null;
    picks.forEach(({ option, value }) => {
      const fieldset = [...modal.querySelectorAll('.wishlist-modal__variant-fieldset')].find(
        (f) => (f.querySelector('.wishlist-modal__variant-label')?.textContent || '').trim() === option
      );
      const input = fieldset && [...fieldset.querySelectorAll('input')].find((i) => i.value === value && !i.disabled);
      if (input && !input.checked) {
        input.checked = true;
        input.dispatchEvent(new Event('change', { bubbles: true }));
      }
    });
  }

  // DOMParser marks <script> elements inert — appending them to the live
  // document never executes them. snippets/size-guide-content.liquid's unit
  // toggle is one inline script, so swap each for a fresh <script> the
  // browser will actually run.
  runInertScripts(root) {
    root.querySelectorAll('script').forEach((old) => {
      const replacement = document.createElement('script');
      for (const { name, value } of old.attributes) replacement.setAttribute(name, value);
      replacement.textContent = old.textContent;
      old.replaceWith(replacement);
    });
  }

  // The Size Guide (sections/wishlist-lightbox-size-guide.liquid, the
  // product's own chart) is fetched on the first tap rather than with every
  // lightbox open. Cached per product, like the extra content.
  async toggleSizeGuide(toggle, panel) {
    if (!panel.hidden) {
      panel.hidden = true;
      toggle.setAttribute('aria-expanded', 'false');
      return;
    }

    if (!panel.dataset.loaded) {
      if (toggle.classList.contains('loading')) return;
      const url = toggle.dataset.url;
      if (!url) return;
      toggle.classList.add('loading');
      try {
        this.sizeGuideCache = this.sizeGuideCache || new Map();
        if (!this.sizeGuideCache.has(url)) {
          const request = fetch(url)
            .then((response) => {
              if (!response.ok) throw new Error(`Size Guide request failed: ${response.status}`);
              return response.text();
            })
            .catch((error) => {
              this.sizeGuideCache.delete(url);
              throw error;
            });
          this.sizeGuideCache.set(url, request);
        }
        const html = await this.sizeGuideCache.get(url);
        const body = new DOMParser().parseFromString(html, 'text/html').querySelector('[data-wishlist-size-guide-body]');
        if (!body) return;
        panel.appendChild(body);
        this.runInertScripts(body);
        panel.dataset.loaded = 'true';
      } catch (error) {
        console.error('Error loading the wishlist Size Guide:', error);
        return;
      } finally {
        toggle.classList.remove('loading');
      }
    }

    panel.hidden = false;
    toggle.setAttribute('aria-expanded', 'true');
    // Open on the product's own chart, as the PDP's sizing modal does.
    const body = panel.querySelector('[data-wishlist-size-guide-body]');
    const anchor = body && body.dataset.anchorId && panel.querySelector(`#${body.dataset.anchorId}`);
    if (anchor) anchor.scrollIntoView({ block: 'start' });
  }

  // The tapped grid card's subtitle line, so the lightbox reads as the card did:
  // card-product.liquid can take a serving count from a metafield, which
  // /products/<handle>.js doesn't carry. Blank for anything but that card.
  cardSubtitle(trigger, productHandle) {
    const card = trigger && trigger.closest && trigger.closest('.card-product[data-product-handle]');
    if (!card || card.dataset.productHandle !== productHandle) return '';
    const line = card.querySelector('[data-card-subtitle] .subtitle');
    return line ? line.textContent.replace(/\s+/g, ' ').trim() : '';
  }

  populateModal(modal, product, cardSubtitle = '') {
    // Reset error message
    this.handleErrorMessage(modal, false);
    
    // Reset button state
    const button = modal.querySelector('.selectForWishlist');
    if (button) {
      button.classList.remove('loading');
      button.setAttribute('aria-busy', 'false');
      button.disabled = false;
      button.classList.remove('is-added', 'is-error');
      const svg = `<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
        <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"></path>
      </svg>`;
      button.innerHTML = svg + ' Add to Wishlist';
      // Every open starts with no ids, so a pick that matches no variant can't
      // save the product shown before this one.
      this.clearButtonVariant(button);
    }
    
    // A pdp-link product reads as on its card: twice the price and pack count
    this.priceMultiplier = this.linkedPdpMultiplier(product);

    // Extract title and subtitle
    const titleParts = product.title.split(' - ');
    const title = titleParts[0];
    const subtitle = cardSubtitle || this.scalePackCount(this.servingsLast(titleParts.slice(1)), this.priceMultiplier);
    
    // Update title and subtitle
    modal.querySelector('.wishlist-modal__title').textContent = title;
    const subtitleEl = modal.querySelector('.wishlist-modal__subtitle');
    if (subtitle) {
      subtitleEl.textContent = subtitle;
      subtitleEl.style.display = 'block';
    } else {
      subtitleEl.style.display = 'none';
    }
    
    // Update badge
    const badge = this.getProductBadge(product);
    const badgeEl = modal.querySelector('.wishlist-modal__badge');
    if (badge) {
      badgeEl.textContent = badge.text;
      if (badge.color) {
        badgeEl.style.backgroundColor = badge.color;
      }
      badgeEl.style.display = 'inline-block';
    } else {
      badgeEl.style.display = 'none';
    }
    
    // Update price
    this.renderPrice(modal, product.variants[0]);
    
    // Update images
    this.updateGallery(modal, product);
    
    // Update variants
    if (product.variants.length > 1) {
      this.updateVariants(modal, product);
    } else {
      // Clear the previous product's options too: hidden is not enough, the
      // variant lookups below read whatever the container still holds.
      const variantsContainer = modal.querySelector('.wishlist-modal__variants');
      variantsContainer.innerHTML = '';
      delete variantsContainer.dataset.variants;
      variantsContainer.style.display = 'none';
      modal.querySelector('.wishlist-modal__size-message').style.display = 'none';
      // Set single variant data on button
      const button = modal.querySelector('.selectForWishlist');
      button.dataset.productId = product.id;
      button.dataset.variantId = product.variants[0].id;
      button.dataset.productTitle = product.title;
      button.dataset.handle = product.handle;
      button.dataset.image = product.featured_image;
      button.dataset.price = product.variants[0].price;
      button.dataset.variantTitle = product.variants[0].title;
    }
    
    // Update description
    if (product.description) {
      this.updateDescription(modal, product.description);
    } else {
      modal.querySelector('.wishlist-modal__description').style.display = 'none';
    }
  }
  
  // Badge text comes from a merchandiser text setting, so build it as a node
  // rather than interpolating the raw string into markup.
  promoBadgeHTML() {
    const badge = document.createElement('span');
    badge.className = 'badge price__badge-sale';
    badge.style.backgroundColor = this.promoBadgeColor;
    badge.style.color = this.promoBadgeTextColor;
    badge.textContent = this.promoBadgeText;
    return badge.outerHTML;
  }

  getProductBadge(product) {
    // Check tags for badges
    for (const tag of product.tags) {
      if (tag.includes('badge:')) {
        const parts = tag.split(':');
        return {
          text: parts[1],
          color: parts[2] || null
        };
      }
      if (tag === 'restocking') {
        return { text: 'Restocking', color: null };
      }
      if (tag === 'soon' && !window.MN?.b2bEarlyAccess) {
        return { text: 'Coming Soon', color: null };
      }
    }
    
    // Check if product is new (within last 30 days)
    const monthAgo = Date.now() - (30 * 24 * 60 * 60 * 1000);
    const createdAt = new Date(product.created_at).getTime();
    if (createdAt >= monthAgo) {
      return { text: 'New', color: null };
    }
    
    return null;
  }
  
  // Used on open (first variant) and after each pick (the picked variant), so a
  // size pick follows the same campaign badge rule as the open.
  renderPrice(modal, variant) {
    const priceContainer = modal.querySelector('.wishlist-modal__price');
    const multiplier = this.priceMultiplier || 1;

    let priceHTML = `<div class="price">`;

    if (variant.compare_at_price && variant.compare_at_price > variant.price) {
      const savings = ((variant.compare_at_price - variant.price) / variant.compare_at_price * 100).toFixed(0);
      priceHTML += `
        <span class="price__regular">${this.formatMoney(variant.price * multiplier)}</span>
        <span class="price__compare">${this.formatMoney(variant.compare_at_price * multiplier)}</span>
      `;
      // A campaign badge (e.g. "BF TAKE 50% OFF") is the one source of truth for
      // the discount message during a campaign — stacking the auto "SAVE X%"
      // badge next to it read like two different discounts (40% + 50%).
      if (!this.promoBadgeText) {
        priceHTML += `<span class="badge price__badge-sale">SAVE ${savings}%</span>`;
      }
    } else {
      priceHTML += `<span class="price__regular">${this.formatMoney(variant.price * multiplier)}</span>`;
    }

    if (this.promoBadgeText) {
      priceHTML += this.promoBadgeHTML();
    }

    priceHTML += `</div>`;
    priceContainer.innerHTML = priceHTML;
  }
  
  updateGallery(modal, product) {
    const swiperWrapper = modal.querySelector('.swiper-wrapper');
    swiperWrapper.innerHTML = '';
    
    // Filter out athlete images
    
    var images = []
    if(product.tags.includes('facet:Protein:true') || product.tags.includes('all products: supplements')){
      images = [product.images[0]];
    }else{
      images = product.images.filter(img => !img.includes('ATHLETE_'));
    }
    
    // Shape the gallery to the photos so they show whole: 4:5 for apparel, 1:1
    // for supplement packshots. component-wishlist-modal.css sizes the box from
    // --wishlist-gallery-ratio and each strip slide from --slide-ratio.
    const ratios = {};
    (product.media || []).forEach((media) => {
      if (media.media_type === 'image' && media.aspect_ratio) ratios[this.imageKey(media.src)] = media.aspect_ratio;
    });
    const gallery = modal.querySelector('.wishlist-modal__gallery');
    const firstRatio = images.length ? ratios[this.imageKey(images[0])] : null;
    if (firstRatio) gallery.style.setProperty('--wishlist-gallery-ratio', firstRatio);
    else gallery.style.removeProperty('--wishlist-gallery-ratio');
    gallery.dataset.count = images.length;

    // /products/<handle>.js gives the originals (1600px+, ~150KB each), and Swiper
    // lays out every slide at once, so ask the CDN for sized copies instead.
    // Built as nodes, not markup: titles can contain a double quote (3" shorts).
    images.forEach((imageUrl, index) => {
      const slide = document.createElement('div');
      slide.className = 'swiper-slide';
      const ratio = ratios[this.imageKey(imageUrl)];
      if (ratio) slide.style.setProperty('--slide-ratio', ratio);
      const img = document.createElement('img');
      img.src = this.sizedImageUrl(imageUrl, 900);
      img.srcset = [600, 900, 1200].map((width) => `${this.sizedImageUrl(imageUrl, width)} ${width}w`).join(', ');
      img.sizes = '(min-width: 768px) 500px, 75vw';
      img.alt = product.title;
      img.loading = index === 0 ? 'eager' : 'lazy';
      slide.appendChild(img);
      swiperWrapper.appendChild(slide);
    });
  }

  // product.images and product.media[].src differ in protocol and query string
  imageKey(url) {
    return String(url).replace(/^https?:/, '').split('?')[0];
  }

  sizedImageUrl(url, width) {
    return `${url}${url.includes('?') ? '&' : '?'}width=${width}`;
  }
  
  updateVariants(modal, product) {
    const variantsContainer = modal.querySelector('.wishlist-modal__variants');
    const sizeMessage = modal.querySelector('.wishlist-modal__size-message');
    variantsContainer.innerHTML = '';
    variantsContainer.style.display = 'block';
    sizeMessage.style.display = 'block';

    // Only the values some variant has. A combined-listing child's .js lists
    // every length in the set (Short, Regular, Tall) but owns just one.
    const optionValues = product.options.map((option, optionIndex) =>
      option.values.filter((value) => product.variants.some((v) => v.options[optionIndex] === value))
    );

    // Name the options the shopper actually has to pick, e.g. "SELECT YOUR COLOR"
    // for a colour-only shaker rather than always "SIZE".
    const optionNames = product.options
      .filter((option, optionIndex) => optionValues[optionIndex].length > 1)
      .map((option) => option.name.toUpperCase());
    sizeMessage.textContent = `SELECT YOUR ${optionNames.join(' AND ') || 'SIZE'} THEN ADD TO WISHLIST`;
    
    // Create variant radios structure
    product.options.forEach((option, optionIndex) => {
      const values = optionValues[optionIndex];
      const fieldset = document.createElement('fieldset');
      fieldset.className = 'wishlist-modal__variant-fieldset';
      
      const legend = document.createElement('legend');
      legend.className = 'wishlist-modal__variant-label';
      legend.textContent = option.name;
      fieldset.appendChild(legend);
      
      const optionsDiv = document.createElement('div');
      optionsDiv.className = 'wishlist-modal__variant-options';
      
      values.forEach((value, valueIndex) => {
        // Find matching variant
        const matchingVariant = product.variants.find(v => v.options[optionIndex] === value);
        
        const label = document.createElement('label');
        label.className = 'wishlist-modal__variant-button';
        
        const input = document.createElement('input');
        input.type = 'radio';
        input.name = `wishlist-option-${product.id}-${option.name}`;
        input.value = value;
        input.dataset.variantId = matchingVariant ? matchingVariant.id : '';
        // Don't pre-select a choice the shopper has to make, but an option with
        // one value left (the child's own length) is picked for them.
        // Don't disable variants based on availability - this is for wishlist, not purchasing
        // if (matchingVariant && !matchingVariant.available) input.disabled = true;
        if (values.length === 1) input.checked = true;
        
        const span = document.createElement('span');
        span.textContent = value;
        
        label.appendChild(input);
        label.appendChild(span);
        optionsDiv.appendChild(label);
      });
      
      fieldset.appendChild(optionsDiv);
      variantsContainer.appendChild(fieldset);
    });
    
    // Store variants data
    variantsContainer.dataset.variants = JSON.stringify(product.variants);
    variantsContainer.dataset.productId = product.id;
    variantsContainer.dataset.productTitle = product.title;
    variantsContainer.dataset.handle = product.handle;
    variantsContainer.dataset.image = product.featured_image;
    
    // Initialize variant selection
    this.initializeVariants(modal);
  }
  
  formatMoney(cents) {
    return '$' + (cents / 100).toFixed(2);
  }

  // A pdp-link product (a Hydration flavour) shows twice its own price on its
  // listing card, with the pack count in its title to match: "14 Pack" reads
  // "28 Pack", while "Variety Pack" and "14 Pack (Limited)" stay. Same rule as
  // snippets/card-product.liquid and src/helpers/linkedPdp.ts; keep them in step.
  linkedPdpMultiplier(product) {
    const tags = Array.isArray(product && product.tags) ? product.tags : [];
    return tags.some((tag) => /^pdp-link:\s*\S/i.test(String(tag).trim())) ? 2 : 1;
  }

  // A serving count in the title ("Tropical Crush - 25 serves") reads
  // "Tropical Crush - 25 Servings", as on the card (card-product.liquid,
  // src/collection/components/ProductCard.tsx parseTitle).
  servingsLast(parts) {
    const servingPart = parts.find((part) => part.trim().toLowerCase().includes('serv'));
    if (!servingPart) return parts.join(' - ');
    const servings = `${servingPart.trim().split(' ')[0]} Servings`;
    const rest = parts.filter((part) => part !== servingPart);
    return rest.length ? `${rest.join(' - ')} - ${servings}` : servings;
  }

  scalePackCount(text, multiplier) {
    if (multiplier === 1 || !text) return text;
    return text
      .split(' - ')
      .map((segment) => {
        const match = /^[ \t\n\v\f\r]*([1-9]\d*)[ \t\n\v\f\r]+(pack)[ \t\n\v\f\r]*$/i.exec(segment);
        return match ? `${Number(match[1]) * multiplier} ${match[2]}` : segment;
      })
      .join(' - ');
  }

  closeModal() {
    const savedJustNow = Boolean(this.saveCloseTimer) || this.promptOnClose;
    this.promptOnClose = false;
    if (this.saveCloseTimer) {
      clearTimeout(this.saveCloseTimer);
      this.saveCloseTimer = null;
    }
    if (!this.activeModal) return;
    
    this.activeModal.classList.remove('active');
    this.activeModal.style.display = 'none';
    document.body.classList.remove('wishlist-modal-open');
    
    // Destroy Swiper instance
    if (this.swiperInstance) {
      this.swiperInstance.destroy(true, true);
      this.swiperInstance = null;
    }

    this.activeModal = null;
    this.activeProductHandle = null;

    // After a save, the guest login prompt waits until the lightbox has gone,
    // whether it closed on its own or the shopper closed it first: it sits below
    // the lightbox (z-index 9999 vs 99999) and would open hidden behind it.
    if (savedJustNow) window.MN?.maybeShowWishlistLoginPrompt?.();
  }

  initSwiper(modal) {
    // The modal was closed before the delay or the Swiper load finished: don't
    // leave a Swiper on a hidden modal.
    if (this.activeModal !== modal) return;

    // Swiper loads on demand now (see __mnLoadSwiper in theme.liquid)
    if (typeof Swiper === 'undefined') {
      if (window.__mnLoadSwiper) {
        window.__mnLoadSwiper().then(() => this.initSwiper(modal));
      } else {
        console.error('Swiper library not loaded and no __mnLoadSwiper helper available');
      }
      return;
    }
    
    const swiperEl = modal.querySelector('.wishlist-modal-swiper');
    
    if (!swiperEl) {
      console.error('Swiper element not found in modal');
      return;
    }
    
    // Destroy existing instance if any
    if (this.swiperInstance) {
      this.swiperInstance.destroy(true, true);
      this.swiperInstance = null;
    }
    
    // Count slides
    const slideCount = swiperEl.querySelectorAll('.swiper-slide').length;
    
    if (slideCount === 0) {
      console.error('No slides found in Swiper');
      return;
    }
    
    // Initialize new Swiper instance
    // Only enable loop if there are 2 or more slides
    try {
      // Slide widths come from the CSS: full width beside the details on wider
      // screens, the photo's own shape in the phone strip so the next one peeks
      // in. Rewind rather than loop, which doesn't suit variable-width slides.
      this.swiperInstance = new Swiper(swiperEl, {
        slidesPerView: 'auto',
        spaceBetween: 8,
        rewind: slideCount > 1,
        centerInsufficientSlides: true,
        watchOverflow: true,
        // Listen on the whole gallery: once the strip has moved, a swipe that
        // starts in the gap between two photos lands outside .swiper-wrapper.
        touchEventsTarget: 'container',
        pagination: {
          el: modal.querySelector('.swiper-pagination'),
          clickable: true,
        },
        navigation: {
          nextEl: modal.querySelector('.swiper-button-next'),
          prevEl: modal.querySelector('.swiper-button-prev'),
        },
        keyboard: {
          enabled: true,
        },
        grabCursor: true,
        on: {
          init: function() {
          },
          slideChange: function() {
          }
        }
      });
      
      
    } catch (error) {
      console.error('Error initializing Swiper:', error);
    }
  }

  initializeVariants(modal) {
    const variantsContainer = modal.querySelector('.wishlist-modal__variants');
    
    if (!variantsContainer || variantsContainer.style.display === 'none') {
      // No variants, product has default variant only
      return;
    }
    
    this.updateUnavailableValues(modal);

    // Only update if variants are selected (they won't be by default anymore)
    if (this.areRequiredVariantOptionsSelected(modal)) {
      this.updateSelectedVariant(modal);
    }
  }

  handleVariantChange(input) {
    const modal = input.closest('.wishlist-modal');
    
    if (!modal) return;
    
    // Clear error message when variant changes
    this.handleErrorMessage(modal, false);
    
    this.updateUnavailableValues(modal);
    this.updateSelectedVariant(modal);
  }

  // Greys out values no variant has alongside the other picks, e.g. XXXXL once
  // Regular is picked on a combined-listing parent whose Regular child stops at
  // XXXL. Stock doesn't count: a sold-out size can still be saved.
  updateUnavailableValues(modal) {
    const variantsContainer = modal.querySelector('.wishlist-modal__variants');
    if (!variantsContainer || !variantsContainer.dataset.variants) return;

    const variantsData = JSON.parse(variantsContainer.dataset.variants);
    const fieldsets = [...variantsContainer.querySelectorAll('.wishlist-modal__variant-fieldset')];
    const picked = fieldsets.map((fieldset) => {
      const checked = fieldset.querySelector('input:checked');
      return checked ? checked.value : null;
    });

    fieldsets.forEach((fieldset, optionIndex) => {
      fieldset.querySelectorAll('input').forEach((input) => {
        // Another length's product: always pickable, it swaps the lightbox
        if (input.dataset.siblingHandle) return;
        input.disabled = !variantsData.some((variant) =>
          variant.options[optionIndex] === input.value &&
          picked.every((value, otherIndex) => otherIndex === optionIndex || value === null || variant.options[otherIndex] === value)
        );
      });
    });
  }

  setAddLabel(button) {
    button.innerHTML = `<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
      <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"></path>
    </svg> Add to Wishlist`;
  }

  // The ids handleAddToWishlist saves.
  clearButtonVariant(button) {
    ['productId', 'variantId', 'productTitle', 'handle', 'image', 'price', 'variantTitle'].forEach((key) => {
      delete button.dataset[key];
    });
  }

  updateSelectedVariant(modal) {
    const variantsContainer = modal.querySelector('.wishlist-modal__variants');
    
    if (!variantsContainer || !variantsContainer.dataset.variants) return;
    
    const variantsData = JSON.parse(variantsContainer.dataset.variants);
    
    // Get all selected options
    const selectedOptions = [];
    const optionInputs = variantsContainer.querySelectorAll('input[type="radio"]:checked');
    
    optionInputs.forEach(input => {
      selectedOptions.push(input.value);
    });
    
    // Find matching variant
    const matchingVariant = variantsData.find(variant => {
      return variant.options.every((option, index) => option === selectedOptions[index]);
    });
    
    if (matchingVariant) {
      // Update the wishlist button with variant data
      const wishlistButton = modal.querySelector('.selectForWishlist');
      
      if (wishlistButton) {
        wishlistButton.dataset.productId = variantsContainer.dataset.productId;
        wishlistButton.dataset.variantId = matchingVariant.id;
        wishlistButton.dataset.productTitle = variantsContainer.dataset.productTitle;
        wishlistButton.dataset.handle = variantsContainer.dataset.handle;
        wishlistButton.dataset.image = variantsContainer.dataset.image;
        wishlistButton.dataset.price = matchingVariant.price;
        wishlistButton.dataset.variantTitle = matchingVariant.title;

        // While a save is in flight the button keeps saying Saving and stays
        // disabled: re-enabling it let a second tap read the first save's
        // optimistic add as done. handleAddToWishlist offers the new pick once
        // the save answers.
        if (!wishlistButton.classList.contains('loading')) {
          // Don't disable button based on availability - this is for wishlist, not purchasing
          wishlistButton.disabled = false;
          wishlistButton.classList.remove('is-added', 'is-error');
          this.setAddLabel(wishlistButton);
        }
      }
      
      // Update price display
      if (matchingVariant.price) this.renderPrice(modal, matchingVariant);
    } else {
      // Nothing to save: drop any ids rather than keep a previous pick's.
      const wishlistButton = modal.querySelector('.selectForWishlist');
      if (wishlistButton) this.clearButtonVariant(wishlistButton);
    }
  }

  updateDescription(modal, description) {
    const descEl = modal.querySelector('.wishlist-modal__description');
    const shortTextEl = descEl.querySelector('.wishlist-modal__description-text');
    const fullTextEl = descEl.querySelector('.wishlist-modal__description-full');
    const showMoreBtn = descEl.querySelector('.wishlist-modal__show-more');
    
    // Create a temporary div to parse the HTML and get text content
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = description;
    const textContent = (tempDiv.textContent || tempDiv.innerText || '').trim();
    
    // If description is empty or whitespace only, hide the section
    if (!textContent) {
      descEl.style.display = 'none';
      return;
    }
    
    // Character limit for truncation (adjust as needed)
    const charLimit = 250;
    
    if (textContent.length > charLimit) {
      // Description is long - truncate it
      const truncatedHTML = this.truncateHTML(description, charLimit);
      
      // Verify truncated HTML has actual text content
      const truncatedDiv = document.createElement('div');
      truncatedDiv.innerHTML = truncatedHTML;
      const truncatedText = (truncatedDiv.textContent || truncatedDiv.innerText || '').trim();
      
      // If truncation resulted in empty content, just show the full description
      if (!truncatedText) {
        shortTextEl.innerHTML = description;
        fullTextEl.style.display = 'none';
        showMoreBtn.style.display = 'none';
      } else {
        shortTextEl.innerHTML = truncatedHTML;
        fullTextEl.innerHTML = description;
        showMoreBtn.style.display = 'inline-block';
        fullTextEl.style.display = 'none';
        showMoreBtn.textContent = 'Show More';
      }
    } else {
      // Description is short - show it all
      shortTextEl.innerHTML = description;
      fullTextEl.style.display = 'none';
      showMoreBtn.style.display = 'none';
    }
    
    descEl.style.display = 'block';
  }
  
  truncateHTML(html, maxLength) {
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = html;
    const text = tempDiv.textContent || tempDiv.innerText;
    
    if (text.length <= maxLength) {
      return html;
    }
    
    // Truncate and add ellipsis
    const truncatedText = text.substring(0, maxLength).trim() + '...';
    
    // Try to preserve basic HTML structure for the truncated content
    let result = '';
    let charCount = 0;
    
    function traverseNodes(node) {
      if (charCount >= maxLength) return;
      
      if (node.nodeType === Node.TEXT_NODE) {
        const remainingChars = maxLength - charCount;
        if (node.textContent.length <= remainingChars) {
          result += node.textContent;
          charCount += node.textContent.length;
        } else {
          result += node.textContent.substring(0, remainingChars) + '...';
          charCount = maxLength;
        }
      } else if (node.nodeType === Node.ELEMENT_NODE) {
        const tagName = node.tagName.toLowerCase();
        result += `<${tagName}>`;
        
        for (let child of node.childNodes) {
          if (charCount >= maxLength) break;
          traverseNodes(child);
        }
        
        result += `</${tagName}>`;
      }
    }
    
    for (let child of tempDiv.childNodes) {
      if (charCount >= maxLength) break;
      traverseNodes(child);
    }
    
    return result;
  }
  
  toggleDescription(button) {
    const modal = button.closest('.wishlist-modal');
    const shortText = modal.querySelector('.wishlist-modal__description-text');
    const fullText = modal.querySelector('.wishlist-modal__description-full');
    
    if (fullText.style.display === 'none' || !fullText.style.display) {
      shortText.style.display = 'none';
      fullText.style.display = 'block';
      button.textContent = 'Show Less';
    } else {
      shortText.style.display = 'block';
      fullText.style.display = 'none';
      button.textContent = 'Show More';
    }
  }

  async handleAddToWishlist(button) {
    const modal = button.closest('.wishlist-modal');
    
    // Check if required variant options are selected
    if (!this.areRequiredVariantOptionsSelected(modal)) {
      this.handleErrorMessage(modal, this.getVariantSelectionMessage(modal));
      return;
    }
    
    const productId = button.dataset.productId;
    const variantId = button.dataset.variantId;
    const productHandle = button.dataset.handle;

    // The picks match no variant. Unreachable while updateUnavailableValues
    // greys those out, but never save without this product's own ids.
    if (!productId || !variantId) {
      this.handleErrorMessage(modal, 'That option isn\'t available. Pick another one.');
      return;
    }

    // A save can take several seconds (Swish took ~11s on 31 Oct 2026), and by
    // the time it answers the shopper may have closed the lightbox or opened
    // another product in it. That lightbox shares this button, so only the
    // open the save was made from gets its feedback and the close.
    const openId = this.openId;
    const stillShowing = () => this.activeModal === modal && this.openId === openId;

    try {
      // Add loading state
      button.classList.add('loading');
      button.setAttribute('aria-busy', 'true');
      button.disabled = true;
      button.innerHTML = `<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
        <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"></path>
      </svg> Saving...`;

      // Saved through MNWishlistStore (assets/wishlist-store.js). A size that's
      // already saved stays saved: the lightbox only ever adds.
      const store = window.MNWishlistStore;
      if (store) {
        if (!store.has(null, variantId)) {
          await store.add({ productHandle, productId, variantId });
        }

        if (!stillShowing()) {
          this.updateWishlistButtonStates(productId, variantId);
          // The close already happened without the guest login prompt: show
          // it now, or when the lightbox now open closes.
          if (this.activeModal) this.promptOnClose = true;
          else window.MN?.maybeShowWishlistLoginPrompt?.();
          return;
        }

        // Another size was picked while this one saved (updateSelectedVariant
        // left the button saying Saving). This one is saved; rather than close,
        // the button now offers the new pick.
        if (button.dataset.variantId !== variantId) {
          this.updateWishlistButtonStates(productId, variantId);
          this.promptOnClose = true;
          button.disabled = false;
          this.setAddLabel(button);
          return;
        }

        // Show success feedback
        button.innerHTML = `<svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor" stroke="currentColor" stroke-width="2">
          <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"></path>
        </svg> Added to Wishlist!`;
        // Saved state takes the size banner's campaign colours (component-wishlist-modal.css)
        button.classList.add('is-added');
        
        // Update all wishlist buttons for this product to show saved state
        this.updateWishlistButtonStates(productId, variantId);
        // No success toast: the button already says it, and the toast sat over
        // the header for 5s after the lightbox closed. Failures still get one.

        // Close modal after 1 second. closeModal clears this timer and then
        // shows the guest login prompt, so a lightbox the shopper closes and
        // replaces within the second isn't closed by this save.
        this.saveCloseTimer = setTimeout(() => this.closeModal(), 1000);
        
      } else {
        throw new Error('Wishlist functionality not available');
      }
      
    } catch (error) {
      console.error('Error adding to wishlist:', error);

      if (!stillShowing()) {
        this.showNotification('Failed to add item to wishlist. Please try again.', 'error');
        return;
      }

      // Show error feedback
      button.innerHTML = `<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
        <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"></path>
      </svg> Failed - Try Again`;
      button.classList.add('is-error');
      
      this.showNotification('Failed to add item to wishlist. Please try again.', 'error');
      
      // Reset button after delay, unless the lightbox has moved on to another
      // product or another save started: either way the button isn't this
      // save's any more, and resetting it mid-save re-enabled a Saving button.
      setTimeout(() => {
        if (!stillShowing() || button.classList.contains('loading') || button.classList.contains('is-added')) return;
        this.setAddLabel(button);
        button.classList.remove('is-error');
        button.disabled = false;
      }, 2000);
      
    } finally {
      // populateModal already reset the button for whatever opened since
      if (stillShowing()) {
        button.classList.remove('loading');
        button.setAttribute('aria-busy', 'false');
      }
    }
  }
  
  updateWishlistButtonStates(productId, variantId) {
    // Find all wishlist buttons on the page for this product
    const allWishlistButtons = document.querySelectorAll('button.wishlistButton');
    
    allWishlistButtons.forEach(button => {
      // Check if this button is for the same product (by checking parent product card)
      const productCard = button.closest('.card-product, .list-card-product');
      if (!productCard) return;
      
      // Find the backup-addToWishlist button that matches our variant
      const variantButtons = productCard.querySelectorAll('.backup-addToWishlist');
      let isThisProduct = false;
      
      variantButtons.forEach(variantBtn => {
        if (variantBtn.dataset.productId == productId && variantBtn.dataset.variantId == variantId) {
          isThisProduct = true;
          // Mark this variant button as saved. Not disabled: that greys a
          // one-variant card's heart, and the card marks saved buttons without
          // it too (markButtonAsSaved in component-list-card-product.js).
          variantBtn.classList.add('is-saved');
          variantBtn.setAttribute('aria-label', 'Saved to wishlist');
          
          const svg = variantBtn.querySelector('svg.wishlist-heart');
          if (svg) {
            svg.classList.add('is-filled');
          }
        }
      });
      
      // If any variant of this product is in wishlist, mark main button as saved
      if (isThisProduct) {
        button.classList.add('is-saved');
        const svg = button.querySelector('svg.wishlist-heart');
        if (svg) {
          svg.classList.add('is-filled');
        }
      }
    });
    
    // Also dispatch a custom event for other components to listen to
    document.dispatchEvent(new CustomEvent('wishlistItemAdded', {
      detail: { productId, variantId }
    }));
  }

  showNotification(message, type = 'error') {
    const className = `wishlist-modal-${type}-notification`;
    let notification = document.querySelector(`.${className}`);
    
    if (!notification) {
      notification = document.createElement('div');
      notification.className = className;
      notification.setAttribute('role', 'alert');
      notification.setAttribute('aria-live', 'polite');
      notification.style.cssText = `
        position: fixed;
        top: 20px;
        right: 20px;
        background: ${type === 'success' ? '#28a745' : '#dc3545'};
        color: white;
        padding: 12px 16px;
        border-radius: 4px;
        z-index: 99999;
        font-size: 14px;
        max-width: 300px;
        box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
        transform: translateX(100%);
        transition: transform 0.3s ease;
      `;
      document.body.appendChild(notification);
    }
    
    notification.textContent = message;
    notification.style.transform = 'translateX(0)';
    
    // Clear any existing timeout
    if (notification._timeout) {
      clearTimeout(notification._timeout);
    }
    
    notification._timeout = setTimeout(() => {
      notification.style.transform = 'translateX(100%)';
      setTimeout(() => {
        if (notification && notification.parentNode) {
          notification.parentNode.removeChild(notification);
        }
      }, 300);
    }, 5000);
  }
}

// Initialize when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    new WishlistModal();
  });
} else {
  new WishlistModal();
}

